import { platformById, safeWebUrl } from "@ondit/publishing";
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useAccess } from "~/core/access/index";
import type { PageDistributionRow } from "~/core/api/distribution";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { readingPath } from "~/modules/analytics/lib/loop";

export function deliveryState(row: PageDistributionRow): string {
	// A saved URL and a proposed calendar slot are not delivery receipts.
	if (["published", "user_reported", "unknown", "failed", "cancelled", "remote_draft"].includes(row.state))
		return row.state;
	if (row.state === "sending" || row.schedule_state === "running") return "sending";
	if (row.schedule_state === "attention") return "attention";
	if (row.schedule_state === "waiting") return "scheduled";
	if (row.kind === "legacy" && ["submitted", "queued"].includes(row.state)) return "unknown";
	return ["ready", "filled", "planned"].includes(row.state) ? row.state : "unknown";
}

export function PageDistribution({ entityId, pageId }: { entityId: string; pageId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "page-distribution", pageId],
		queryFn: () => api.pageDistribution(entityId, pageId),
		...read,
		refetchInterval: 10_000,
	});
	const path = `${readingPath(`/w/${entityId}`, "distribution")}?tab=prepare&page=${encodeURIComponent(pageId)}`;
	return (
		<section className="stack border-l border-(--border) pl-(--s-4)" aria-label={t("pages.distribution")}>
			<Async state={state}>
				{({ rows, platforms }) =>
					rows.length ? (
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead>{t("distribution.platform")}</TableHead>
									<TableHead>{t("table.status")}</TableHead>
									<TableHead>{t("pages.deliveryTime")}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((row) => {
									const status = deliveryState(row);
									const ownLabel = [
										"scheduled",
										"planned",
										"published",
										"user_reported",
										"attention",
										"unknown",
									].includes(status);
									return (
										<TableRow key={`${row.kind}-${row.id}`}>
											<TableCell className="wrap">
												{row.platform_name ||
													platforms.find((p) => p.id === row.platform)?.name ||
													platformById(row.platform)?.name ||
													row.platform}
												<div className="faint">
													{t("pages.revision", { n: row.revision })}
													{row.kind === "legacy" ? ` · ${t("pages.delivery.legacy")}` : ""}
												</div>
											</TableCell>
											<TableCell>
												<span
													className={
														status === "published"
															? "pill pill-success"
															: ["unknown", "failed", "attention"].includes(status)
																? "pill pill-warning"
																: "pill"
													}
												>
													{t(`${ownLabel ? "pages.delivery" : "publishing.platform.state"}.${status}`)}
												</span>
											</TableCell>
											<TableCell className="mono">
												{fmt.dateTime(
													locale,
													["scheduled", "planned"].includes(status) ? row.planned_at : row.updated_at,
												)}
											</TableCell>
											<TableCell>
												<div className="record-actions">
													{row.published_url && safeWebUrl(row.published_url) ? (
														<a href={row.published_url} target="_blank" rel="noreferrer">
															{t("publishing.platform.result")}
														</a>
													) : null}
													{row.kind !== "legacy" ? (
														<Button variant="default" size="sm" asChild>
															<Link
																to={`${path}&platform=${encodeURIComponent(row.platform)}${row.kind === "task" ? `&task=${encodeURIComponent(row.id)}` : ""}`}
															>
																{t("pages.distribution")}
															</Link>
														</Button>
													) : null}
												</div>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					) : (
						<p className="hint">{t("pages.distributionEmpty")}</p>
					)
				}
			</Async>
			{editor ? (
				<div>
					<Button variant="default" size="sm" asChild>
						<Link to={path}>{t("pages.distribute")}</Link>
					</Button>
				</div>
			) : null}
		</section>
	);
}
